import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiBriefcase, FiGlobe, FiTarget } = FiIcons;

const buyerProfiles = [
    {
        icon: FiUsers,
        role: "CTOs & Technical Leaders",
        desc: "Looking to modernise legacy systems, adopt AI, or scale infrastructure with a trusted engineering partner."
    },
    {
        icon: FiBriefcase,
        role: "Founders & CEOs",
        desc: "Building their first enterprise product or upgrading operations with custom software and intelligent automation."
    },
    {
        icon: FiTarget,
        role: "Operations & IT Heads",
        desc: "Seeking to eliminate manual workflows, integrate siloed systems, and improve data-driven decision making."
    }
];

const segments = [
    { label: "Mid-Market Companies", detail: "50–500 employees scaling operations" },
    { label: "Growth-Stage Startups", detail: "Seed to Series B building core products" },
    { label: "Enterprise Divisions", detail: "Business units needing agile delivery" }
];

const WhoWeServe = () => {
    return (
        <section className="py-24 relative z-10 bg-white">
            <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-bold mb-4 text-slate-900">
                        Who We <span className="text-gradient-cyan">Serve</span>
                    </h2>
                    <p className="text-slate-600 max-w-2xl mx-auto text-lg">
                        We partner with ambitious businesses across India and the United Kingdom who need senior engineering talent — without the overhead of building an in-house team.
                    </p>
                </div>

                {/* Buyer Roles */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                    {buyerProfiles.map((profile, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="p-8 bg-white border border-slate-200 hover:border-cyan-300 transition-all group shadow-sm hover:shadow-md"
                        >
                            <div className="w-12 h-12 bg-cyan-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <SafeIcon icon={profile.icon} className="text-2xl text-cyan-600" />
                            </div>
                            <h4 className="text-lg font-bold text-slate-900 mb-3">{profile.role}</h4>
                            <p className="text-sm text-slate-600 leading-relaxed">{profile.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Company Segments + Geos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-6">Company Profiles We Work With</h3>
                        <div className="space-y-4">
                            {segments.map((seg, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-start gap-4 p-4 bg-slate-50 border border-slate-100"
                                >
                                    <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0"></div>
                                    <div>
                                        <span className="font-bold text-slate-900 text-sm">{seg.label}</span>
                                        <p className="text-xs text-slate-500 mt-1">{seg.detail}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="p-8 bg-slate-50 border border-slate-100"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <SafeIcon icon={FiGlobe} className="text-2xl text-cyan-600" />
                            <h3 className="text-xl font-bold text-slate-900">Geographic Presence</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">India</h4>
                                <p className="text-xs text-slate-500 leading-relaxed">Development headquarters and primary delivery centre.</p>
                            </div>
                            {/* <div>
                                <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">United Kingdom</h4>
                                <p className="text-xs text-slate-500 leading-relaxed">Client-facing operations and consulting engagements.</p>
                            </div> */}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default WhoWeServe;
